import { Building2, Home, Hotel, BedDouble, Building, Briefcase, Trees, LucideIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

const ethiopianCities = [
  'Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle',
  'Dire Dawa', 'Jimma', 'Gondar', 'Dessie', 'Bishoftu',
  'Shashamane', 'Arba Minch', 'Harar', 'Debre Markos', 'Nekemte',
];

const propertyTypeTiles: { value: string; label: string; icon: LucideIcon }[] = [
  { value: 'villa', label: 'Villa', icon: Home },
  { value: 'apartment', label: 'Apartment', icon: Building2 },
  { value: 'flat', label: 'Flat', icon: Building },
  { value: 'penthouse', label: 'Penthouse', icon: Hotel },
  { value: 'room', label: 'Room', icon: BedDouble },
  { value: 'commercial', label: 'Commercial', icon: Briefcase },
  { value: 'land', label: 'Land', icon: Trees },
];

interface Props {
  listingType: 'sale' | 'rent';
  setListingType: (v: 'sale' | 'rent') => void;
  propertyType: string;
  setPropertyType: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  subCity: string;
  setSubCity: (v: string) => void;
  woreda: string;
  setWoreda: (v: string) => void;
  address: string;
  setAddress: (v: string) => void;
}

export function Step1Purpose(p: Props) {
  return (
    <>
      <div>
        <Label className="mb-2 block text-sm font-semibold">Purpose *</Label>
        <div className="grid grid-cols-2 gap-3">
          {(['sale', 'rent'] as const).map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => p.setListingType(opt)}
              className={cn(
                'h-14 rounded-xl border-2 text-sm font-semibold transition-all',
                p.listingType === opt
                  ? 'border-primary bg-primary/5 text-primary shadow-sm'
                  : 'border-border hover:border-primary/40',
              )}
            >
              {opt === 'sale' ? 'For Sale' : 'For Rent'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="mb-2 block text-sm font-semibold">Property Type *</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {propertyTypeTiles.map(t => {
            const Icon = t.icon;
            const active = p.propertyType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => p.setPropertyType(t.value)}
                className={cn(
                  'flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 p-3 transition-all',
                  active
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/40 text-muted-foreground',
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>City *</Label>
          <Select value={p.city} onValueChange={p.setCity}>
            <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
            <SelectContent>
              {ethiopianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Sub City</Label><Input value={p.subCity} onChange={e => p.setSubCity(e.target.value)} placeholder="e.g. Bole" /></div>
        <div><Label>Woreda</Label><Input value={p.woreda} onChange={e => p.setWoreda(e.target.value)} placeholder="e.g. Woreda 03" /></div>
      </div>

      <div>
        <Label>Address / Landmark</Label>
        <Input value={p.address} onChange={e => p.setAddress(e.target.value)} placeholder="Near Edna Mall, Bole Road" />
      </div>
    </>
  );
}
